import { BCMSDefaultModel, BCMSRecord, ModalOpen, useRoute, useWorkflowStep } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import BillerView from "compid/shared/views/BillerView";
import BillerPaymentView from "compid/shared/views/BillerView/components/views/BillerPaymentView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import { DatePickerView } from "compid/ui/DatePicker";
import UIAmountInput from "compid/ui/UIAmountInput";
import UIButton from "compid/ui/UIButton";
import UICheckbox from "compid/ui/UICheckbox";
import UIPhoneInput from "compid/ui/UIPhoneInput";
import UIRadio from "compid/ui/UIRadio";
import UITextArea from "compid/ui/UITextArea";
import UITextInput from "compid/ui/UITextInput";
import { getDefault, getWorkflowSteps } from "compid/workflows/PaymentInquiryWorkflow";
import { PaymentInquiry } from "model";
import React from "react";
import "./styles.css";

/**
 * @function PaymentInquiryPage
 * @return {React.Component}
 * @exported true
 * @description The main component for PaymentInquiryPage
 */

export default function PaymentInquiryPage(props) {
  const navigate = useRoute();
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  const workflowState = workflowStep.getStepState;
  const paymentInquiry = workflowStep.read("paymentInquiry");
  const payment = paymentInquiry.payment;
  // All confirmation numbers for payments will be the same for now.
  const confirmationNumber = "TN2RH-1LNHX";
  const account = payment.from;
  const biller = payment.to;
  var lateFeesAccessed =  paymentInquiry.lateFeesAccessed;
  lateFeesAccessed = workflowState.userSetLateFeesAccessed ? lateFeesAccessed : undefined;
  var financeChargesAssessed = paymentInquiry.financeChargesAssessed;
  financeChargesAssessed = workflowState.userSetFinanceChargesAssessed ? financeChargesAssessed : undefined;
  const paymentArriveByDate = payment.date.format('M d, Y');
  const paymentWithdrawByDate = payment.date.format('M d, Y');

  const fiName = props.fiName ?? "[fiName]";
  const receiptDate = props.receiptDate ?? "[receiptDate]";
  const withdrawnDate = props.withdrawnDate ?? "[withdrawnDate]";
  const accountName = props.accountName ?? "[accountName]";
  const accountNickname = props.accountNickname ?? "[accountNickname]";
  const accountLastFour = props.accountLastFour ?? "[accountLastFour]";

  const update = prop => value => {
    if(prop === "lateFeesAmount") {
      workflowStep.update(["userSetLateFeesAmount"], true);
    } else if(prop === "financeChargesAmount") {
      workflowStep.update(["userSetFinanceChargesAmount"], true);
    } else if(prop === "contactDate") {
      workflowStep.update(["userSetContactDate"], true);
    }
    console.log(prop, value)
    workflowStep.update(["paymentInquiry", [prop]], value);
  };

  // Radio buttons pass data with multiple arguments.
  const updateRadio = prop => (value, label) => {
    if(prop === "lateFeesAccessed") {
      workflowStep.update(["userSetLateFeesAccessed"], true);
    } else if(prop === "financeChargesAssessed") {
      workflowStep.update(["userSetFinanceChargesAssessed"], true);
    }
    
    workflowStep.update(["paymentInquiry", [prop]], label === "Yes" ? true : false);
  }

  // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
  const onLeave = () => navigate(-1);
  const sendInquiry = () => {
    const defaultPaymentInquiry = BCMSDefaultModel.paymentInquiry();
    var updatedPaymentInquiry = BCMSDefaultModel.mergeRecords(paymentInquiry, defaultPaymentInquiry);
    updatedPaymentInquiry = PaymentInquiry.init(updatedPaymentInquiry);
    //return;
    workflowStep.update(["paymentInquiry"], updatedPaymentInquiry);

    BCMSRecord.batch([
      // Create a new payment inquiry.
      PaymentInquiry.create(updatedPaymentInquiry),
    ], workflowStep.globalController);

    // Route to the next step
    try {
      window.scrollTo(0, 0);
      workflowStep.workflow.nextStep(workflowStep.getStepState);
    } catch(error) {
      workflowStep.navigate(`/paymentInquiryConfirmation/${ payment.id }/${ workflowStep.currentStep + 1 }`, { state: workflowStep.getStepState });
    }
  };



  // Dates after today are disabled in the calendar.
  const futureDateConditionalDisable = date => date.isFuture() ? true : false;

  return(
    <PageView className="payment-inquiry-page">
      <p className="page-sub-header semi-bold-text">PAYMENT INQUIRY</p>

      <PageLayoutContainer>
        <CenterColumnContainer>
          <BillerView
           biller={ biller } 
           paymentInfo={
            <BillerPaymentView 
                amount={ payment.amount }
                status={ payment.status }
                date={ payment.formatDate }
                showAutopay={ payment.hasRelatedAutopay }
                description={<>Last<span className="show-on-desktop payment">Payment</span><span className="show-on-mobile payment">pmt</span></>} />
        }
           />

            <div className="inset-container">
              <p className="top-text">Customer service typically responds to an inquiry within three to four business days. Check your messages for a response.</p>
              <p className="top-text">Your check was mailed to { biller.name } for receipt by { paymentArriveByDate }.</p>
              <p className="top-text">Money was withdrawn from your account on { paymentWithdrawByDate }. The payment was applied on { paymentWithdrawByDate }.</p>
            
              <TableView>
                <TableRowView label="Confirmation" data={ confirmationNumber } />
                <TableRowView label="Pay from" data={ account.name + " " + account.number } />
              </TableView>

              <div className="form-group">
                <div className="form-row reason-for-inquiry">
                  <CustomSelect label="What's the reason for your inquiry?" value={ paymentInquiry.reason } helpText="" onChange={ update("reason") }>
                    <CustomSelectItem value="biller did not credit payment">Biller did not credit payment</CustomSelectItem>
                    <CustomSelectItem value="biller credited payment as late">Biller credited payment as late</CustomSelectItem>
                    <CustomSelectItem value="question about this payment">Question about this payment</CustomSelectItem>
                    <CustomSelectItem value="wrong amount">Wrong amount</CustomSelectItem>
                    <CustomSelectItem value="question about how payments are processed">Question about how payments are processed</CustomSelectItem>
                    <CustomSelectItem value="question about canceling a payment">Question about canceling a payment</CustomSelectItem>
                  </CustomSelect>
                </div>

                <div className="form-row late-fees-radio">
                  <b className="sub-text">Were any late fees accessed?</b>
                  <div className="flex-container flex-horizontal align-center">
                    <UIRadio label="No" name="lateFees" value={ lateFeesAccessed === false } onChange={ updateRadio("lateFeesAccessed") } />
                    <UIRadio label="Yes" name="lateFees" value={ lateFeesAccessed === true } onChange={ updateRadio("lateFeesAccessed") } />
                  </div>
                </div>

                { lateFeesAccessed && 
                  <div className="form-row late-fees-amount-input">
                    <UIAmountInput placeHolder="If yes, how much for the late fees?" 
                    value={ workflowState.userSetLateFeesAmount ? paymentInquiry.lateFeesAmount.format():  "" } 
                    onChange={ update("lateFeesAmount") } />
                  </div>
                }

                { lateFeesAccessed && 
                  <div className="form-row late-fees-checkbox">
                    <div className="flex-container flex-horizontal align-center">
                      <UICheckbox checked={ paymentInquiry.billerWaivedLateFeeCharge } onChange={ update("billerWaivedLateFeeCharge") } />
                      <p className="flex-width">Click here if the biller waived the late fee charges.</p>
                    </div>
                  </div>
                }

        
                  <div className="form-row finance-changes-radio">
                    <b className="sub-text">Were finance charges assessed?</b>
                    <div className="flex-container flex-horizontal align-center">
                      <UIRadio label="No" name="financeCharges" value={ financeChargesAssessed === false } onChange={ updateRadio("financeChargesAssessed") } />
                      <UIRadio label="Yes" name="financeCharges" value={ financeChargesAssessed === true } onChange={ updateRadio("financeChargesAssessed") } />
                    </div>
                  </div>
                

                { financeChargesAssessed &&
                  <div className="form-row finance-charges-amount-input">
                    <UIAmountInput placeHolder="If yes, how much for the finance changes?"
                    value={ workflowState.userSetFinanceChargesAmount ? paymentInquiry.financeChargesAmount.format() : "" }
                    onChange={ update("financeChargesAmount") }/>
                  </div>
                }

                { financeChargesAssessed &&
                  <div className="form-row finance-charges-checkbox">
                    <div className="flex-container flex-horizontal align-center finance-charge-checkbox">
                      <UICheckbox checked={ paymentInquiry.billerWaivedFinanceCharges } onChange={ update("billerWaivedFinanceCharges") } />
                      <p className="flex-width">Click here if the biller waived the finance charges.</p>
                    </div>
                  </div>
                }

                <div className="form-row contact-text">
                  <p className="sub-text">If you contacted { biller.name } about this payment, please complete the following contact information.</p>
                </div>

                <div className="form-row contact-name">
                  <UITextInput placeHolder="Contact name" value={ paymentInquiry.contactName } helpText="" onChange={ update("contactName") } />
                </div>

                <div className="form-row contact-phone">
                  <UIPhoneInput placeHolder="Contact phone number" value={ paymentInquiry.phoneNumber } helpText="" onChange={ update("phoneNumber") } />
                </div>

                <div className="form-row contact-date">
                  <DatePickerView disableWeekends={false} showWords={false} disableToday={false} dateLabel="Contact date" enablePast="true" conditionalDisable={ futureDateConditionalDisable }
                  setDate={ workflowState.userSetContactDate ? paymentInquiry.contactDate : "" }
                  onChange={ update("contactDate") } />
                </div>

                <div className="form-row additional-comments">
                  <UITextArea label="Additional comments" resizerClassName="" value={ paymentInquiry.additionalComments } onChange={ update("additionalComments") } placeholder="" />
                </div>
                
                <ButtonContainer className="form-button-container">
                  <UIButton className="secondary-button">
                    <ModalOpen id="WantToLeaveModal" data={ {onLeave: onLeave} }>Cancel</ModalOpen>
                  </UIButton>
                  <UIButton className="primary-button" onClick={ sendInquiry }>Send inquiry</UIButton>
                </ButtonContainer>
              </div>
            </div>
        </CenterColumnContainer>
      </PageLayoutContainer>
    </PageView>
  );
}